// กรอบ webview (รถ "ระบบเดิม") อยู่ที่ WebviewCell · กรอบยิงตรงถามกล้องที่ระดับกรอบก่อน
// แล้วเลือกว่าจะเปิดทุกกล้องพร้อมกัน (แบบหน้าเดิม) หรือกล้องเดียวที่เลือกได้

import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Cell, CameraGrid, CellBody } from '../styles/wall'
import { CameraName, ChannelName, Header } from '../styles/cell'
import { CELL, MODE } from '../lib/constants'
import { camerasToOpenTogether, isOfflineCode } from '../lib/cellState'
import { fill } from '../dict'
import useCameraDiscovery from '../lib/useCameraDiscovery'
import useCellSession from '../lib/useCellSession'
import PlayerHost from '../players/PlayerHost'
import CellHeader, { cameraLabel } from './CellHeader'
import CellOverlay from './CellOverlay'
import CellStatus from './CellStatus'
import WebviewCell from './WebviewCell'

export default function VideoCell(props) {
  const { vehicle, mode } = props
  const [muted, setMuted] = useState(true)
  const isDirect = mode === MODE.DIRECT
  const found = useCameraDiscovery({
    enabled: isDirect && !!vehicle,
    vehicleId: vehicle ? vehicle.id : null,
  })
  const common = { ...props, muted, setMuted }

  if (!vehicle) return <EmptyCell {...props} />
  if (!isDirect) return <WebviewCell {...props} />

  if (!found.discovery) {
    return (
      <DiscoveringCell
        {...common}
        error={found.error}
        onRediscover={found.rediscover}
      />
    )
  }
  const together = camerasToOpenTogether(found.discovery)
  if (together) {
    return (
      <MultiCameraCell
        {...common}
        discovery={found.discovery}
        cameras={together}
        onRediscover={found.rediscover}
      />
    )
  }
  return (
    <SingleCameraCell
      {...common}
      discovery={found.discovery}
      onRediscover={found.rediscover}
    />
  )
}

/** ช่องที่ยังไม่มีรถ */
function EmptyCell({ slotIndex, txt }) {
  return (
    <Cell>
      <Header>
        <ChannelName>{fill(txt.slot, { n: slotIndex + 1 })}</ChannelName>
      </Header>
      <CellBody>
        <CellStatus state={CELL.EMPTY} txt={txt} />
      </CellBody>
    </Cell>
  )
}

/** ถามกล้องอยู่ หรือถามไม่สำเร็จ — ยังไม่มี session */
function DiscoveringCell({
  vehicle,
  muted,
  setMuted,
  selected,
  txt,
  error,
  onSelect,
  onRemove,
  onRediscover,
}) {
  let state = CELL.STARTING
  if (error) state = isOfflineCode(error) ? CELL.OFFLINE : CELL.ERROR

  return (
    <Cell selected={selected} onClick={onSelect}>
      <CellHeader
        label={vehicle.plate_no}
        state={state}
        muted={muted}
        cameras={[]}
        txt={txt}
        onToggleMute={() => setMuted(m => !m)}
        onRetry={onRediscover}
        onClose={onRemove}
      />
      <CellBody>
        <CellStatus
          state={state}
          error={error}
          vehicleId={vehicle.id}
          txt={txt}
          onRetry={onRediscover}
        />
      </CellBody>
    </Cell>
  )
}

function SingleCameraCell({
  vehicle,
  muted,
  setMuted,
  selected,
  txt,
  discovery,
  onSelect,
  onRemove,
  onRediscover,
}) {
  const cell = useCellSession({ vehicle, discovery, onRediscover })
  const showPlayer = !!cell.source.url && cell.state !== CELL.STOPPED

  return (
    <Cell selected={selected} onClick={onSelect}>
      <CellHeader
        label={vehicle.plate_no}
        speed={cell.speed}
        state={cell.state}
        muted={muted}
        cameras={cell.cameras}
        camera={cell.camera}
        txt={txt}
        onToggleMute={() => setMuted(m => !m)}
        onCameraChange={cell.changeCamera}
        onRetry={cell.retry}
        onClose={onRemove}
      />

      <CellBody>
        {showPlayer && (
          <PlayerHost
            key={cell.playerKey}
            protocol={cell.source.protocol}
            url={cell.source.url}
            muted={muted}
            {...cell.handlers}
            onAutoMuted={() => setMuted(true)}
          />
        )}

        {cell.state === CELL.PLAYING && (
          <CellOverlay plateNo={vehicle.plate_no} />
        )}

        <CellStatus
          state={cell.state}
          error={cell.error}
          endReason={cell.endReason}
          waitingSince={cell.waitingSince}
          sessionId={cell.sessionId}
          vehicleId={vehicle.id}
          txt={txt}
          onRetry={cell.retry}
        />
      </CellBody>
    </Cell>
  )
}

/** หัวกรอบรวมทุกกล้อง: กำลังเล่นถ้ามีกล้องไหนเล่นอยู่ · KB/s รวมทุกกล้อง · ลองใหม่ = ถามกล้องใหม่ทั้งกรอบ */
function MultiCameraCell({
  vehicle,
  muted,
  setMuted,
  selected,
  txt,
  discovery,
  cameras,
  onSelect,
  onRemove,
  onRediscover,
}) {
  const [reports, setReports] = useState({})
  const onReport = useCallback((camera, report) => {
    setReports(prev => ({ ...prev, [camera]: report }))
  }, [])

  const states = cameras.map(c => (reports[c.camera] || {}).state)
  const state =
    states.indexOf(CELL.PLAYING) !== -1
      ? CELL.PLAYING
      : states[0] || CELL.STARTING
  const speeds = cameras
    .map(c => (reports[c.camera] || {}).speed)
    .filter(n => typeof n === 'number')
  const speed = speeds.length ? speeds.reduce((a, b) => a + b, 0) : null

  return (
    <Cell selected={selected} onClick={onSelect}>
      <CellHeader
        label={vehicle.plate_no}
        speed={speed}
        state={state}
        muted={muted}
        cameras={[]}
        txt={txt}
        onToggleMute={() => setMuted(m => !m)}
        onRetry={onRediscover}
        onClose={onRemove}
      />
      <CameraGrid count={cameras.length}>
        {cameras.map(cam => (
          <CameraTile
            key={cam.camera}
            vehicle={vehicle}
            discovery={discovery}
            camera={cam.camera}
            name={cameraLabel(cam, txt)}
            muted={muted}
            setMuted={setMuted}
            txt={txt}
            onReport={onReport}
          />
        ))}
      </CameraGrid>
    </Cell>
  )
}

/** กล้อง 1 ตัวในกรอบที่เปิดทุกกล้อง — session ของตัวเอง ลองใหม่เฉพาะกล้องนี้ */
function CameraTile({
  vehicle,
  discovery,
  camera,
  name,
  muted,
  setMuted,
  txt,
  onReport,
}) {
  const cell = useCellSession({ vehicle, discovery, camera })
  const showPlayer = !!cell.source.url && cell.state !== CELL.STOPPED

  useEffect(() => {
    onReport(camera, { state: cell.state, speed: cell.speed })
  }, [camera, cell.state, cell.speed, onReport])

  return (
    <CellBody>
      {showPlayer && (
        <PlayerHost
          key={cell.playerKey}
          protocol={cell.source.protocol}
          url={cell.source.url}
          muted={muted}
          {...cell.handlers}
          onAutoMuted={() => setMuted(true)}
        />
      )}

      <CameraName>{name}</CameraName>

      {cell.state === CELL.PLAYING && (
        <CellOverlay plateNo={vehicle.plate_no} />
      )}

      <CellStatus
        state={cell.state}
        error={cell.error}
        endReason={cell.endReason}
        waitingSince={cell.waitingSince}
        sessionId={cell.sessionId}
        vehicleId={vehicle.id}
        txt={txt}
        onRetry={cell.retry}
      />
    </CellBody>
  )
}

VideoCell.propTypes = {
  vehicle: PropTypes.object,
  /** 0-based — ใช้แค่ตั้งชื่อกรอบว่าง */
  slotIndex: PropTypes.number.isRequired,
  mode: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  txt: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
  onRemove: PropTypes.func,
}
